import { EventConfigurationKeys } from '@/enums/EventConfigurationKeys';
import { IEventConfiguration } from '@/resources/upgrade/EventConfiguration';

interface INavigationItem {
  id: EventConfigurationKeys;
  route: string;
  configKey: keyof IEventConfiguration;
  labelKey: keyof IEventConfiguration;
  // Matches the entries the backend sends in eventConfiguration.menu_items_order.
  orderKey: string;
}

export const NAVIGATION_ITEMS: Array<INavigationItem> = [
  {
    id: EventConfigurationKeys.Reception,
    route: '/reception',
    configKey: 'reception_page',
    labelKey: 'reception_page_label',
    orderKey: 'reception',
  },
  {
    id: EventConfigurationKeys.Schedule,
    route: '/schedule',
    configKey: 'schedule_page',
    labelKey: 'schedule_page_label',
    orderKey: 'schedule',
  },
  {
    id: EventConfigurationKeys.MainStage,
    route: '/main-stage',
    configKey: 'main_stage_page',
    labelKey: 'main_stage_page_label',
    orderKey: 'main_stage',
  },
  {
    id: EventConfigurationKeys.Sessions,
    route: '/sessions',
    configKey: 'sessions_page',
    labelKey: 'sessions_page_label',
    orderKey: 'sessions',
  },
  {
    id: EventConfigurationKeys.Posters,
    route: '/posters',
    configKey: 'posters_page',
    labelKey: 'posters_page_label',
    orderKey: 'posters',
  },
  {
    id: EventConfigurationKeys.Expo,
    route: '/expo',
    configKey: 'expo_page',
    labelKey: 'expo_page_label',
    orderKey: 'expo',
  },
  {
    id: EventConfigurationKeys.Lounge,
    route: '/lounge',
    configKey: 'lounge_page',
    labelKey: 'lounge_page_label',
    orderKey: 'lounge',
  },
  {
    id: EventConfigurationKeys.Community,
    route: '/community',
    configKey: 'community_page',
    labelKey: 'community_page_label',
    orderKey: 'community',
  },
  {
    id: EventConfigurationKeys.Helpdesk,
    route: '/help-desk',
    configKey: 'helpdesk_page',
    labelKey: 'helpdesk_page_label',
    orderKey: 'helpdesk',
  },
];

export type NavigationItemsType = typeof NAVIGATION_ITEMS;

/**
 * The nav item a path belongs to. Resolved against NAVIGATION_ITEMS order rather than the
 * event's display order, so nested paths like /schedule/sessions/1 resolve to the outer
 * section (schedule) instead of lighting up both it and sessions.
 */
export function getActiveNavigationRoute(currentRoute: string): string | null {
  return NAVIGATION_ITEMS.find(({ route }) => currentRoute.includes(route))?.route ?? null;
}

// Nav items enabled for this event, ordered by eventConfiguration.menu_items_order when the
// backend provides one. Items missing from that list (e.g. it hasn't been set yet) keep
// NAVIGATION_ITEMS' own order and sort after the ones that are explicitly placed.
export function getOrderedNavigationItems(eventConfiguration: IEventConfiguration): NavigationItemsType {
  const enabledItems = NAVIGATION_ITEMS.filter(({ configKey }) => eventConfiguration[configKey]);
  const order = eventConfiguration.menu_items_order;

  if (!order) {
    return enabledItems;
  }

  return [...enabledItems].sort((a, b) => {
    const aIndex = order.indexOf(a.orderKey);
    const bIndex = order.indexOf(b.orderKey);

    if (aIndex === -1) {
      return bIndex === -1 ? 0 : 1;
    }

    if (bIndex === -1) {
      return -1;
    }

    return aIndex - bIndex;
  });
}
